import Header from '../layouts/Header';
import React, { useState, useEffect } from 'react';
import { Container, Button, ButtonGroup, Form, Table, Modal, Alert, Row, Col, Spinner, Card } from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
import DormLocationPicker from './DormLocationPicker';

const DormitoryBuildings = () => {
  const [villages, setVillages] = useState([]);
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [genderFilter, setGenderFilter] = useState('all');

  const [formData, setFormData] = useState({
    dorm_code: '',
    dorm_name: '',
    capacity_total: 5,
    dorm_status: 'active',
    dorm_note: '',
    dorm_lat: '',
    dorm_lng: ''
  });

  // Fetch villages
  const fetchVillages = async () => {
    try {
      const response = await axiosInstance.get('/api/dormitory/villages');
      if (response.data.success) {
        setVillages(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedVillage(response.data.data[0].village_id);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลหมู่หอพักไม่สำเร็จ');
    }
  };

  // Fetch buildings for selected village
  const fetchBuildings = async () => {
    if (!selectedVillage) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/dormitory/villages/${selectedVillage}/buildings`);
      if (response.data.success) {
        setBuildings(response.data.data);
        setError('');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลอาคารหอพักไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVillages();
  }, []);

  useEffect(() => {
    fetchBuildings();
  }, [selectedVillage]);

  const handleVillageChange = (e) => {
    setSelectedVillage(e.target.value);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'capacity_total' ? parseInt(value) || 0 : value
    }));
  };

  const resetForm = () => {
    setFormData({
      dorm_code: '',
      dorm_name: '',
      capacity_total: 5,
      dorm_status: 'active',
      dorm_note: '',
      dorm_lat: '',
      dorm_lng: ''
    });
    setIsEdit(false);
    setEditId(null);
  };

  const handleShowModal = (building = null) => {
    if (building) {
      setFormData({
        dorm_code: building.dorm_code,
        dorm_name: building.dorm_name,
        capacity_total: building.capacity_total,
        dorm_status: building.dorm_status,
        dorm_note: building.dorm_note || '',
        dorm_lat: building.dorm_lat ?? '',
        dorm_lng: building.dorm_lng ?? ''
      });
      setIsEdit(true);
      setEditId(building.dorm_id);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSave = async () => {
    try {
      setError('');
      setSuccess('');

      if (!formData.dorm_code || !formData.dorm_name || !formData.capacity_total) {
        setError('กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน');
        return;
      }

      // Empty coordinate inputs must go to the DB as NULL, not ''
      const cleaned = {
        ...formData,
        dorm_lat: formData.dorm_lat === '' ? null : Number(formData.dorm_lat),
        dorm_lng: formData.dorm_lng === '' ? null : Number(formData.dorm_lng)
      };

      const payload = {
        ...cleaned,
        tb_dormitory_village_village_id: selectedVillage
      };

      let response;
      if (isEdit) {
        response = await axiosInstance.put(`/api/dormitory/buildings/${editId}`, cleaned);
      } else {
        response = await axiosInstance.post('/api/dormitory/buildings', payload);
      }

      if (response.data.success) {
        setSuccess(isEdit ? 'อัปเดตหอพักเรียบร้อยแล้ว' : 'เพิ่มหอพักเรียบร้อยแล้ว');
        fetchBuildings();
        handleCloseModal();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'บันทึกข้อมูลหอพักไม่สำเร็จ';
      setError(errorMsg);
      
      if (err.response?.data?.code === 'DUPLICATE_DORM_CODE') {
        setError('รหัสหอพักนี้มีอยู่แล้ว กรุณาใช้รหัสอื่น');
      } else if (err.response?.data?.code === 'VILLAGE_NOT_FOUND') {
        setError('ไม่พบหมู่หอพักที่เลือก');
      }
    }
  };

  const getOccupancyPercentage = (current, total) => {
    return Math.round((current / total) * 100);
  };

  const getStatusColor = (current, total) => {
    const percentage = getOccupancyPercentage(current, total);
    if (percentage >= 100) return 'danger';
    if (percentage >= 80) return 'warning';
    return 'success';
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'active':
        return <span className="badge bg-success">ใช้งาน</span>;
      case 'inactive':
        return <span className="badge bg-secondary">ปิดใช้งาน</span>;
      case 'maintenance':
        return <span className="badge bg-warning text-dark">ปรับปรุง</span>;
      default:
        return status;
    }
  };

  const filteredVillages = villages.filter(v =>
    genderFilter === 'all' || v.village_gender === genderFilter
  );

  const handleGenderFilterChange = (value) => {
    setGenderFilter(value);
    const filtered = villages.filter(v => value === 'all' || v.village_gender === value);
    if (filtered.length > 0) {
      setSelectedVillage(filtered[0].village_id);
    } else {
      setSelectedVillage(null);
    }
  };

  const selectedVillageName = villages.find(v => v.village_id == selectedVillage)?.village_name || '';

  return (
    <React.Fragment>
      <Header />
    <main className="main main-app p-3 p-lg-4">
      <Container fluid className="px-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>จัดการหอพัก</h2>
        <Button variant="primary" onClick={() => handleShowModal()}>
          + เพิ่มหอพัก
        </Button>
      </div>

      {/* Village Selector */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={4}>
              <Form.Label><strong>ประเภทหอพัก</strong></Form.Label>
              <div>
                <ButtonGroup>
                  <Button variant={genderFilter === 'all' ? 'primary' : 'outline-primary'} onClick={() => handleGenderFilterChange('all')}>ทั้งหมด</Button>
                  <Button variant={genderFilter === 'm' ? 'primary' : 'outline-primary'} onClick={() => handleGenderFilterChange('m')}>👨 ชาย</Button>
                  <Button variant={genderFilter === 'f' ? 'primary' : 'outline-primary'} onClick={() => handleGenderFilterChange('f')}>👩 หญิง</Button>
                </ButtonGroup>
              </div>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-0">
                <Form.Label><strong>เลือกหมู่หอพัก</strong></Form.Label>
                <Form.Select
                  value={selectedVillage || ''}
                  onChange={handleVillageChange}
                  disabled={filteredVillages.length === 0}
                >
                  {filteredVillages.length === 0 ? (
                    <option value="">ไม่มีหมู่หอพัก</option>
                  ) : (
                    filteredVillages.map(v => (
                      <option key={v.village_id} value={v.village_id}>
                        {v.village_name} ({v.village_gender === 'm' ? '👨 ชาย' : '👩 หญิง'})
                      </option>
                    ))
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              {selectedVillageName && (
                <div className="pb-1">
                  <strong>หมู่หอพักปัจจุบัน:</strong> {selectedVillageName}
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">กำลังโหลด...</span>
          </Spinner>
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-light">
            <tr>
              <th style={{ width: '15%' }}>รหัส</th>
              <th style={{ width: '25%' }}>เลขที่หอพัก</th>
              <th style={{ width: '12%' }}>ความจุรวม</th>
              <th style={{ width: '18%' }}>การเข้าพัก</th>
              <th style={{ width: '12%' }}>สถานะ</th>
              <th style={{ width: '10%' }}>หมายเหตุ</th>
              <th style={{ width: '8%' }}>จัดการ</th>
            </tr>
          </thead>
          <tbody>
            {buildings.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-4 text-muted">
                  ยังไม่มีหอพักในหมู่หอพักนี้ กรุณาเพิ่มรายการแรกเพื่อเริ่มต้น
                </td>
              </tr>
            ) : (
              buildings.map(building => {
                const occupancyPct = getOccupancyPercentage(building.current_occupancy, building.capacity_total);
                const vacant = building.capacity_total - building.current_occupancy;
                
                return (
                  <tr key={building.dorm_id}>
                    <td><strong>{building.dorm_code}</strong></td>
                    <td>{building.dorm_name}</td>
                    <td className="text-center">{building.capacity_total}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div style={{ width: '100px' }}>
                          <div 
                            className={`progress bg-light`}
                            style={{ height: '20px' }}
                          >
                            <div
                              className={`progress-bar bg-${getStatusColor(building.current_occupancy, building.capacity_total)}`}
                              style={{ width: `${occupancyPct}%` }}
                            >
                              {occupancyPct}%
                            </div>
                          </div>
                        </div>
                        <small className="text-nowrap">
                          {building.current_occupancy}/{building.capacity_total} (ว่าง {vacant} ที่)
                        </small>
                      </div>
                    </td>
                    <td>{statusLabel(building.dorm_status)}</td>
                    <td>{building.dorm_note ? building.dorm_note.substring(0, 30) + '...' : '-'}</td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleShowModal(building)}
                      >
                        แก้ไข
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      )}

      {/* Modal for Create/Edit */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? 'แก้ไขอาคารหอพัก' : 'เพิ่มอาคารหอพัก'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                รหัสหอพัก <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="dorm_code"
                value={formData.dorm_code}
                onChange={handleInputChange}
                placeholder="เช่น M101 หรือ F201"
                disabled={isEdit}
                maxLength="5"
              />
              <Form.Text className="text-muted">รหัสอ้างอิงที่ไม่ซ้ำกันของหอพัก</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                เลขที่หอพัก <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="dorm_name"
                value={formData.dorm_name}
                onChange={handleInputChange}
                placeholder="เช่น 5/1 หรือ 15/2"
                maxLength="255"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    ความจุรวม <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="capacity_total"
                    value={formData.capacity_total}
                    onChange={handleInputChange}
                    min="1"
                    max="999"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>สถานะ</Form.Label>
                  <Form.Select
                    name="dorm_status"
                    value={formData.dorm_status}
                    onChange={handleInputChange}
                  >
                    <option value="active">ใช้งาน</option>
                    <option value="inactive">ปิดใช้งาน</option>
                    <option value="maintenance">ปรับปรุง</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>หมายเหตุ</Form.Label>
              <Form.Control
                as="textarea"
                name="dorm_note"
                value={formData.dorm_note}
                onChange={handleInputChange}
                rows={3}
                placeholder="รายละเอียดเพิ่มเติม..."
                maxLength="500"
              />
            </Form.Group>

            <hr />
            <DormLocationPicker
              lat={formData.dorm_lat}
              lng={formData.dorm_lng}
              onChange={(lat, lng) => setFormData(prev => ({ ...prev, dorm_lat: lat, dorm_lng: lng }))}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            ยกเลิก
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {isEdit ? 'บันทึกการแก้ไข' : 'เพิ่มหอพัก'}
          </Button>
        </Modal.Footer>
      </Modal>
      </Container>
    </main>
  </React.Fragment>
  );
};

export default DormitoryBuildings;
